import $ from "jquery";
import { configureDialogClose, configureDialogTriggers } from "./common/dialog.utils";
import { configurePopupKeyboardNavigation } from "@/common/popup.utils";
import { createFocusTrap } from "focus-trap";

// Immediately load jQuery into the global scope (`window`).
//
// This is done early as some of our legacy jQuery plugins are written in a way that
// causes the plugin to try to load itself into jQuery at the time that the plugin is
// imported (FKA a side-effect). This often requires that jQuery be available in the
// global scope at the time.
window.jQuery = $;
window.$ = $;

// This module is imported 'dynamically' (using `import()` instead of `import ...`).
// This seems to delay the later imports, which prevents some JS console messages
// ("Autofocus processing was blocked because a document already has a focused element")
// from appearing in accessibility test logs. We used to ``await`` on the magnific popup
// import here, but now that we've removed that package we need to await on
// __something__.
await import("@/common/utils");

import { runOnDOMContentLoaded, configurePrintButtonEvents } from "./common/utils";
import { initialisePostHog, capturePageview } from "./setup/posthog.config";
import posthog from "posthog-js";

// Initialise PostHog as early as possible
initialisePostHog();

runOnDOMContentLoaded(() => {
  /**
   * Capture PostHog pageview event for server-rendered page loads.
   *
   * This manually tracks the initial page load since we've disabled PostHog's
   * automatic pageview capture. Vue Router navigation events are tracked elsewhere.
   */
  capturePageview();

  /**
   * Capture print events in PostHog.
   *
   * We're hooking media instead of using 'beforeprint' because there are some vague
   * reports that 'beforeprint' isn't always reliable.
   */
  const mediaQueryList = window.matchMedia("print");
  mediaQueryList.addEventListener("change", (event) => {
    if (event.matches) {
      posthog.capture("print", {
        $current_url: window.location.href,
      });
    }
  });

  /**
   * Add js-enabled class to body to signal that JS is enabled.
   */
  document.body.classList.add("js-enabled");

  /**
   * Add ajax-loading class to body to indicate that an Ajax request is in progress.
   */
  $.ajaxSetup({ traditional: true });
  $("body")
    .ajaxStart(function () {
      document.body.classList.add("ajax-loading");
    })
    .ajaxStop(function () {
      document.body.classList.remove("ajax-loading");
    });

  /**
   * Primary navigation bar dropdown menus
   */

  let dropdownTriggers = document.querySelectorAll(".dropdown-trigger");

  function openDropdownMenu(triggerElement) {
    let dropdownMenu = triggerElement.nextElementSibling;
    dropdownMenu.style.display = "block";
    triggerElement.setAttribute("aria-expanded", "true");
    triggerElement.setAttribute("aria-controls", dropdownMenu.id);
  }

  function closeDropdownMenu(triggerElement) {
    let dropdownMenu = triggerElement.nextElementSibling;
    dropdownMenu.style.display = "none";
    triggerElement.setAttribute("aria-expanded", "false");
    triggerElement.removeAttribute("aria-controls");
  }

  function toggleDropdownMenu(triggerElement) {
    let dropdownMenu = triggerElement.nextElementSibling;

    if (dropdownMenu.style.display === "block") {
      closeDropdownMenu(triggerElement);
    } else {
      openDropdownMenu(triggerElement);
    }
  }

  dropdownTriggers.forEach((trigger) => {
    // Handling trigger click (or touch)
    trigger.addEventListener("click", function (e) {
      // Prevent event from reaching to document when trigger is clicked
      e.stopPropagation();
      toggleDropdownMenu(trigger);
    });

    // Find the closest parent with the 'has-menu' class and configure mouse enter/leave
    // behaviour
    let menuParent = trigger.closest(".has-menu");
    menuParent.addEventListener("mouseenter", () => openDropdownMenu(trigger));
    menuParent.addEventListener("mouseleave", () => closeDropdownMenu(trigger));

    let dropdownMenu = trigger.nextElementSibling; // Find the dropdown menu
    if (dropdownMenu) {
      // Configure tab/esc behaviour
      configurePopupKeyboardNavigation(trigger, dropdownMenu, closeDropdownMenu);
      // Prevent event from reaching to document when dropdown is clicked
      dropdownMenu.addEventListener("click", (e) => e.stopPropagation());
    }
  });

  // Handling outside click to close all open dropdowns
  document.addEventListener("click", function (e) {
    if (!e.target.classList.contains("dropdown-trigger")) {
      dropdownTriggers.forEach(closeDropdownMenu);
    }
  });

  /**
   * Disable 'fake' links
   */

  document.querySelectorAll('a[href="#"]').forEach((link) => {
    link.addEventListener("click", function (e) {
      e.preventDefault();
    });
  });

  /**
   * Brightpath Teacher's Ruler / 'Scale' display
   */

  // Set the height of the segment row based on the descriptor range
  $(".ruler-segment").each(function (i, el) {
    var numberSegment = $(el).find(".ruler-segment-row").length;

    if (i == 0) {
      var height = numberSegment * 50 - 50;
    } else {
      var height = numberSegment * 50;
    }

    $(el)
      .find(".ruler-segment-description")
      .css({
        height: height - 20,
      });
  });

  // add classes for the size of the ruler pip
  $(".ruler-value[data-value$=0]").parent().addClass("large-unit");

  /**
   * Modals
   *
   * Not our Vue.js ones! These are modals defined using the `modal-container` CSS
   * class.
   */
  const modalContainerToModalMap = new Map();
  const modalContainerObservers = new Map();
  const modalFocusTraps = new Map();

  const onModalOpened = (modal) => {
    modalFocusTraps.get(modal)?.activate();
  };

  const onModalClosed = (modal) => {
    modalFocusTraps.get(modal)?.deactivate();
  };

  /**
   * Set up every modal on the page.
   *
   * This includes:
   * - Installing a focus trap on every modal
   * - Making the modal element programmatically focusable
   * - Activating/deactivating the focus trap when the modal is opened/closed
   */
  document.querySelectorAll(".modal-container").forEach((modalContainer) => {
    const modal = modalContainer.querySelector(".modal");
    if (!modal) return;

    // Track the modal element for this container
    modalContainerToModalMap.set(modalContainer, modal);

    // Ensure the modal element supports focus
    modal.setAttribute("tabindex", "-1");

    // Initialise the focus trap for the modal
    modalFocusTraps.set(
      modal,
      createFocusTrap(modal, {
        // Focus the modal so that a screen reader sees it first
        initialFocus: () => modal,
        // Ensue that clicking outside the modal closes it
        clickOutsideDeactivates: true,
        onDeactivate: closeModal,
      }),
    );

    // If the modal is initially open, immediately activate the focus trap
    if (modalContainer.classList.contains("open")) onModalOpened(modal);

    // Watch for the open class being added/removed so we can activate/deactivate the
    // focus trap as required
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const container = mutation.target;
        const modal = modalContainerToModalMap.get(container);
        const wasOpen = mutation.oldValue.split(" ").includes("open");
        const isOpen = container.classList.contains("open");
        // Modal was just opened
        if (!wasOpen && isOpen) onModalOpened(modal);
        // Modal was just closed
        else if (wasOpen && !isOpen) onModalClosed(modal);
      });
    });
    observer.observe(modalContainer, {
      attributes: true,
      attributeFilter: ["class"],
      attributeOldValue: true,
    });
    modalContainerObservers.set(modalContainer, observer);
  });

  /**
   * Close the open modal.
   *
   * @param {Object} options - Options for the function.
   * @param {boolean} options.expectModal - Whether to expect a modal to be open. If
   * `true`, the function will throw an error if no modal is found to close. If `false`,
   * the function will silently return if no modal is found to close.
   *
   * @throws {Error} If no modal is found to close and `options.expectModal` is `true`.
   */
  function closeModal(options = { expectModal: true }) {
    const element = document.querySelector(".modal-container");
    if (element === null && options.expectModal) {
      throw Error("No modal to close.");
    }
    if (element) element.classList.remove("open");
  }

  document.querySelectorAll(".button-close-modal").forEach((button) => {
    button.addEventListener("click", function (event) {
      event.preventDefault();
      closeModal();
    });
  });

  // Close Modal via Esc key
  document.onkeydown = function (event) {
    if (event.key === "Escape") {
      closeModal({ expectModal: false });
    }
  };

  /**
   * Native dialog setup
   */
  configureDialogTriggers();
  configureDialogClose();

  /**
   * Print buttons
   */
  configurePrintButtonEvents();

  /**
   * Focus on the first invalid form field, or the form level errors/warnings panel if
   * no field has an error.
   *
   * This should aid users of screen readers to jump immediately to the relevant page
   * content when an invalid form is submitted.
   */
  (
    document.querySelector("[aria-invalid='true']") ||
    document.querySelector(".errors-panel") ||
    document.querySelector(".warnings-panel")
  )?.focus();
});
